import React from 'react';
import Markdown from '@/components/Markdown';
import { TEXT_TYPE } from '@/types';
import { Box, Typography } from '@mui/material';
import { arrayOf, oneOf, shape, string } from 'prop-types';
import FormCard from './FormCard';

const LeadFormBlock = ({
  align,
  formIcon,
  formName,
  title,
  leadFormInputs,
  buttonText,
  textBlockHeading,
  textBlock,
  submittedIcon,
  submittedTitle,
  submittedDescription,
  submittedButtonText,
  theme,
  leadCaptureType,
  leadCaptureRecordType,
  leadCaptureSource,
}) =>
  leadFormInputs?.length > 0 && (
    <Box display="flex" justifyContent={align}>
      <Box
        order={align === 'right' ? 1 : 2}
        flexDirection="column"
        sx={{ px: 4, pt: 1, display: { xs: 'none', md: 'flex' } }}
        maxWidth={600}
        textAlign="justify"
        mx="auto"
      >
        {textBlockHeading && (
          <Typography
            color={theme.titleColor}
            variant={textBlockHeading.variant}
            component={textBlockHeading.component}
            align={textBlockHeading.alignment}
          >
            {textBlockHeading.text}
          </Typography>
        )}

        <Markdown source={textBlock} theme={theme} />
      </Box>
      <FormCard
        formIcon={formIcon}
        formName={formName}
        title={title}
        inputs={leadFormInputs}
        buttonText={buttonText}
        submittedIcon={submittedIcon}
        submittedTitle={submittedTitle}
        submittedDescription={submittedDescription}
        submittedButtonText={submittedButtonText}
        leadType={leadCaptureType}
        leadRecordType={leadCaptureRecordType}
        leadSource={leadCaptureSource}
      />
    </Box>
  );

LeadFormBlock.propTypes = {
  /** Where to align the form */
  align: oneOf(['left', 'right']).isRequired,

  /** The url of the image of the pre-submission icon and its alt */
  formIcon: shape({
    url: string.isRequired,
    alt: string.isRequired,
  }).isRequired,
  // Used for tracking submissions
  formName: string.isRequired,

  /** The title of the form pre-submission. */
  title: string.isRequired,
  /** The values for a given input field. One object is one input field.
   */
  leadFormInputs: arrayOf(
    shape({
      apiInputName: string,
      /** The placeholder value in the input field. */
      label: string,
      /** See See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input for possible input types. */
      type: string,
      required: string,
      defaultValue: string,
      display: string,
    }),
  ).isRequired,
  /** The text of the form button pre-submission */
  buttonText: string.isRequired,

  /** The heading of the text block. */
  textBlockHeading: TEXT_TYPE,
  /** The markdown text to be displayed next to the form. */
  textBlock: shape({
    compiledSource: string.isRequired,
  }).isRequired,

  /** The url of the image of the post-submission icon and its alt */
  submittedIcon: shape({
    url: string.isRequired,
    alt: string.isRequired,
  }).isRequired,
  /** The title of the form post-submission. */
  submittedTitle: string.isRequired,
  /** The description post-submission. */
  submittedDescription: string.isRequired,
  /** The text of the form button post-submission */
  submittedButtonText: string.isRequired,

  /** The lead record type for a lead form. */
  leadCaptureType: string.isRequired,
  /** The lead type for a lead form. */
  leadCaptureRecordType: string.isRequired,
  /** The lead record source for a lead form. */
  leadCaptureSource: string,

  theme: shape({}).isRequired,
};

LeadFormBlock.defaultProps = {
  textBlockHeading: null,
  leadCaptureSource: 'Bellhop Site',
};

export default LeadFormBlock;
